<template>
  <div>
    <BaseModal v-model="showModal" size="lg">
      <StatementDocumentViewer :pdfUrl="pdfUrl" :modelValue="documentReadConfirmed"
        @update:modelValue="documentReadConfirmed = $event" />
    </BaseModal>
    <b-form @submit.prevent="onSubmit">
      <AlertDismiss ref="alert" />
      <div v-if="noTemplates">
        <div class="card">
          <div class="card-body text-center">
            Aún no se han creado plantillas de declaración.
          </div>
        </div>
      </div>

      <b-overlay v-else :show="isfetching" variant="white">
        <div class="mb-3">
          <label>Formulario de declaración:</label>
          <select class="form-control" v-model="selectedTemplate">
            <option disabled :value="null">-- Seleccione un formulario --</option>
            <option v-for="template in templates" :key="template.id" :value="template">
              {{ template.name }}
            </option>
          </select>
        </div>
      </b-overlay>
      <div v-if="selectedTemplate" class="mt-3">
        <hr>

        <transition name="fade">
          <div v-if="isQuestionDataReady">
            <StatementQuestion
              v-for="(question, index) in selectedTemplate.questions"
              :key="question.id"
              :question="question"
              :index="index"
              :modelValue="answers[index]"
              :path="[index]"
              :disabled="isUploading"
              @update:modelValue="handleAnswerUpdate"
              />
          </div>
        </transition>

        <div class="mt-3">
          <div class="mb-3">
            <div v-if="!documentReadConfirmed" class="d-flex align-items-center">
              <b-icon
                icon="x-circle-fill"
                variant="danger"
                class="me-1"
              ></b-icon>
              <small class="text-muted">Debe confirmar haber leído el documento antes de enviar.</small>
            </div>
            <div v-if="documentReadConfirmed" class="d-flex align-items-center">
              <b-icon
                icon="check-circle-fill"
                variant="success"
                class="me-1"
              ></b-icon>
              <small class="text-success">Documento leído y confirmado.</small>
            </div>
          </div>
          
          <div class="d-flex align-items-center gap-3">
            <button class="btn btn-outline-primary" @click="openDocumentModal" type="button">
              <b-icon icon="file-earmark-pdf" class="mr-1" />
              <span class="ms-2">Ver documento</span>
            </button>
            
            <button class="btn btn-primary" type="submit" :disabled="isUploading || !documentReadConfirmed">
              <b-spinner v-if="isUploading" small />
              Enviar
            </button>
          </div>
        </div>

      </div>

    </b-form>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { EventNames } from '@/plugins/enums';
import AlertDismiss from "@/components/shared/Alert-dismiss.vue";
import BaseModal from '@/components/shared/Base-modal.vue';
import StatementDocumentViewer from '@/components/shared/StatementDocumentViewer.vue';
import StatementQuestion from '@/components/PepStatements/PepStatementMenu/PepStatementCreate/Pep-statement-question.vue';

export default {
  components: {
    AlertDismiss,
    BaseModal,
    StatementDocumentViewer,
    StatementQuestion,
  },

  data() {
    return {
      isfetching: true,
      showModal: false,
      agreed: false,
      pdfUrl: "",
      selectedTemplate: null,
      templates: [],
      answers: [],
      isUploading: false,
      documentReadConfirmed: false,
    };
  },

  computed: {
    noTemplates() {
      return !this.isfetching && this.templates.length === 0;
    },
    isQuestionDataReady() {
      return (
        this.selectedTemplate?.questions &&
        this.answers.length === this.selectedTemplate.questions.length
      );
    }
  },

  async mounted() {
    this.isfetching = true;
    await this.getTemplates();
    this.isfetching = false;
  },

  methods: {
    ...mapActions([
      "getPepDocumentUrl",
      "getAllPepStatementTemplatesForUser",
      "savePepStatement",
    ]),

    async getTemplates() {
      this.isfetching = true;
      const result = await this.getAllPepStatementTemplatesForUser();

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.isfetching = false;
        return;
      }

      this.templates = result.data;
      this.isfetching = false;
    },

    async openDocumentModal() {
      this.showModal = true;
      const result = await this.getPepDocumentUrl({ route: this.selectedTemplate.document.route });

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.showModal = false;
        return;
      }

      this.pdfUrl = result.data;
    },

    buildAnswerStructure(questions) {
      return questions.map(question => {
        const answer = {
          questionId: question.id,
          isTrue: null,
          value: "",
          details: [],
          subAnswers: (question.childrenQuestionDTO || []).map(subQ => ({
            questionId: subQ.id,
            isTrue: null,
            value: "",
            details: [],
          }))
        };
        return answer;
      });
    },

    resetAnswers() {
      this.documentReadConfirmed = false;
      if (this.selectedTemplate?.questions) {
        this.answers = this.buildAnswerStructure(this.selectedTemplate.questions);
      } else {
        this.answers = [];
      }
    },

    handleAnswerUpdate(event) {
      const { path, value: newValue } = event;
      if (path.length === 1) {
        const mainIndex = path[0];
        this.answers.splice(mainIndex, 1, newValue);
      } else if (path.length === 2) {
        const mainIndex = path[0];
        const subIndex = path[1];
        this.answers[mainIndex].subAnswers.splice(subIndex, 1, newValue);
      } else { // shouldnt happen but just in case
        console.error("Error updating answer: Invalid path or index", path, newValue);
      }
    },

    async onSubmit() {
      this.isUploading = true;
      const questionPeps = [];

      for (const parentAnswer of this.answers) {
        // Parent question payload
        const parentPayload = {
          questionId: parentAnswer.questionId,
          isCorrect: parentAnswer.isTrue,
        };
        if (parentAnswer.isTrue) {
          parentPayload.societieRequests = parentAnswer.details;
        }
        questionPeps.push(parentPayload);

        // If parent is answered true, process its children
        if (parentAnswer.isTrue && parentAnswer.subAnswers && parentAnswer.subAnswers.length > 0) {
          for (const childAnswer of parentAnswer.subAnswers) {
            const childPayload = {
              questionId: childAnswer.questionId,
              isCorrect: childAnswer.isTrue,
            };
            // Child societieRequests are added if child is answered true
            if (childAnswer.isTrue) {
              childPayload.societieRequests = childAnswer.details;
            }
            questionPeps.push(childPayload);
          }
        }
      }

      const payload = {
        templatePepId: this.selectedTemplate.id,
        questionPeps: questionPeps,
      };

      const result = await this.savePepStatement(payload);

      if (result.isError) {
        this.$refs.alert.showAlert(result.message);
        this.isUploading = false;
        return;
      }

      this.$refs.alert.showAlert("Declaración enviada", "success");
      this.$eventBus.$emit(EventNames.CreatedPepStatement);
      this.isUploading = false;
      this.resetAnswers();
    },
  },

  watch: {
    selectedTemplate() {
      this.resetAnswers();
    }
  }
};
</script>

<style scoped>
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s ease-in-out;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>